<template>
  <CollapsibleSection
    v-if="getValue('formation_lieu') === 'INTER'"
    title="Session inter-entreprises"
    icon="fas fa-users"
    context="quote"
    storage-key="inter-info"
    :bare="bare"
  >
    <DetailItem label="Ville">
      <select
        v-if="isEditing"
        v-model="editedQuote.inter_ville"
        @change="$emit('ville-change')"
        class="form-control"
      >
        <option value="">-- Sélectionner une ville --</option>
        <option v-for="ville in availableVilles" :key="ville" :value="ville">{{ ville }}</option>
      </select>
      <template v-else>
        <span v-if="!isMissing(quote.inter_ville)">{{ quote.inter_ville }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>

    <!-- Message d'erreur si la session sélectionnée est complète -->
    <div v-if="selectedSession && isSessionComplete" class="error-message detail-item-full">
      <i class="fas fa-exclamation-triangle"></i>
      <div>
        <p><strong>Il ne reste plus de place disponible pour cette session de formation.</strong></p>
        <p>Merci de sélectionner une autre date.</p>
      </div>
    </div>

    <DetailItem v-if="getValue('inter_ville')" label="Session">
      <select
        v-if="isEditing"
        v-model="editedQuote.inter_session_id"
        class="form-control"
      >
        <option value="">-- Sélectionner une session --</option>
        <option v-for="session in availableSessions" :key="session.id" :value="session.id" :disabled="session.complete">
          {{ session.dates_display || 'Date non disponible' }}
        </option>
      </select>
      <template v-else>
        <span v-if="!isMissing(quote.inter_session_id)">{{ getSessionDisplayText(quote.inter_session_id) }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!--
      L'effectif inter se saisit dans le bloc Formation, avec la durée et le
      lieu dont il dépend : c'est lui qui multiplie le tarif à la place. Le
      redemander ici ferait deux saisies pour une seule colonne.
    -->
    <DetailItem v-if="getValue('inter_session_id')" label="Tarif">
      <template v-if="selectedSession">
        {{ formatPrice(selectedSession.tarif) }} / apprenant
      </template>
      <template v-else-if="quote.inter_tarif">
        {{ quote.inter_tarif }}€ / apprenant
      </template>
      <DetailEmpty v-else />
    </DetailItem>
    <!-- Le commentaire du client se lit et se complète dans le bloc
         « La demande », avec la formation qu'il décrit. -->
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { computed, watch } from 'vue'
import { CollapsibleSection, DetailItem, DetailEmpty } from '../data'
import { formatPrice } from '../../utils/currencyFormatters.js'

const props = defineProps({
  /**
   * Mode « nu » : le contenu seul, sans carte ni en-tête repliable. La fenêtre
   * « Analyse du besoin » v2 n'affiche qu'une section à la fois et porte
   * elle-même son titre — la carte y ferait un cadre dans un cadre.
   * Additif : sans la prop, le rendu est exactement celui d'avant.
   */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  },
  availableVilles: {
    type: Array,
    default: () => []
  },
  availableSessions: {
    type: Array,
    default: () => []
  },
  selectedSession: {
    type: Object,
    default: null
  },
  getValue: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['ville-change'])

const isMissing = (value) => {
  return !value || value === '' || value === null || value === undefined
}

const getSessionDisplayText = (sessionId) => {
  if (!sessionId) return ''
  const session = props.availableSessions.find(s => s.id === sessionId)
  if (session && session.dates_display) {
    return session.dates_display
  }
  return sessionId
}

const isSessionComplete = computed(() => props.selectedSession?.complete === true)

watch(isSessionComplete, (isComplete) => {
  if (isComplete && props.isEditing && props.editedQuote.inter_session_id) {
    props.editedQuote.inter_session_id = ''
    props.editedQuote.inter_tarif = null
  }
})
</script>

<style scoped>
.form-control {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-border-dark);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  font-family: inherit;
}

.form-control:focus {
  outline: 2px solid var(--color-info);
  outline-offset: 2px;
  border-color: var(--color-info);
  box-shadow: 0 0 0 3px var(--color-info-lighter);
}

.detail-item-full {
  grid-column: 1 / -1;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  background: #fef2f2;
  border-left: 4px solid #ef4444;
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
}

.error-message i {
  color: #ef4444;
  font-size: 1.25rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.error-message p {
  margin: 0;
  color: #991b1b;
  font-weight: 500;
}

.error-message p:not(:first-of-type) {
  margin-top: var(--spacing-xs);
}
</style>
